import { type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  CLONE_META_DETAIL_TEXT_SX,
  CLONE_META_DETAILS_LINK_SX,
  CLONE_META_VALUE_WRAPPER_SX,
  CLONE_TOOLTIP_SLOT_PROPS,
  TableMetaInlineRow,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { Box, Link, Tooltip, type Theme } from '@/ui';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '@/ui/composites/table/tableTooltip';
import type { SxProps } from '@/ui/primitives';

type TableBreakdownLineProps = {
  icon: ReactElement;
  content: ReactNode;
  ariaLabel?: string;
  href?: string | null;
  disableLinks?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  linkSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  valueWrapperSx?: SxProps<Theme>;
  rowTitle?: string;
  valueTitle?: string | null;
  disableOverflowTooltip?: boolean;
};

const toSxProps = (value: SxProps<Theme> | undefined): SxProps<Theme> => value ?? [];

const mergeSxProps = (...values: Array<SxProps<Theme> | undefined>): SxProps<Theme> => {
  const resolvedValues: Array<SxProps<Theme>> = [];

  values.forEach((value) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      resolvedValues.push(...(value as Array<SxProps<Theme>>));
      return;
    }

    resolvedValues.push(value);
  });

  return resolvedValues as SxProps<Theme>;
};

const extractTooltipFontSx = (styles: SxProps<Theme>): SxProps<Theme> => {
  const resolvedStyles = Array.isArray(styles) ? styles : [styles];
  const fontStyles = resolvedStyles.filter(
    (style) =>
      !Array.isArray(style) && style !== null && typeof style === 'object' && 'fontFamily' in style,
  );

  return fontStyles as SxProps<Theme>;
};

export const TableBreakdownLine = ({
  icon,
  content,
  ariaLabel,
  href = null,
  disableLinks = false,
  onClick,
  linkSx,
  contentSx,
  valueWrapperSx,
  rowTitle,
  valueTitle,
  disableOverflowTooltip = false,
}: TableBreakdownLineProps): ReactElement => {
  const resolvedValueWrapperSx: SxProps<Theme> = mergeSxProps(
    CLONE_META_VALUE_WRAPPER_SX,
    valueWrapperSx,
  );
  const resolvedLinkSx = linkSx ?? CLONE_META_DETAILS_LINK_SX;
  const resolvedContentSx = toSxProps(contentSx);
  const tooltipSx = extractTooltipFontSx(
    mergeSxProps(resolvedLinkSx, resolvedContentSx, valueWrapperSx),
  );
  const contentLabel =
    typeof content === 'string' || typeof content === 'number' ? String(content) : null;
  const renderedValue =
    disableLinks || href === null ? (
      ariaLabel ? (
        <span aria-label={ariaLabel}>{content}</span>
      ) : (
        content
      )
    ) : (
      <Link
        component={RouterLink}
        to={href}
        underline="hover"
        color="inherit"
        onClick={onClick}
        sx={resolvedLinkSx}
      >
        {ariaLabel ? <span aria-label={ariaLabel}>{content}</span> : content}
      </Link>
    );

  const wrappedContent = valueTitle ? (
    <Tooltip
      title={valueTitle}
      arrow
      enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
      slotProps={CLONE_TOOLTIP_SLOT_PROPS}
      disableHoverListener={!valueTitle}
    >
      <Box component="span" sx={resolvedValueWrapperSx}>
        {renderedValue}
      </Box>
    </Tooltip>
  ) : contentLabel && !disableOverflowTooltip ? (
    <OverflowTooltip
      title={contentLabel}
      tooltipSx={tooltipSx}
      wrapperSx={resolvedValueWrapperSx}
      inline
    >
      {renderedValue}
    </OverflowTooltip>
  ) : (
    <Box component="span" sx={resolvedValueWrapperSx}>
      {renderedValue}
    </Box>
  );

  return (
    <TableMetaInlineRow
      icon={
        rowTitle ? (
          <Tooltip
            title={rowTitle}
            arrow
            enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
            slotProps={CLONE_TOOLTIP_SLOT_PROPS}
          >
            <Box component="span">{icon}</Box>
          </Tooltip>
        ) : (
          icon
        )
      }
      textSx={CLONE_META_DETAIL_TEXT_SX}
      rowSx={{ width: '100%', maxWidth: '100%' }}
      contentSx={contentSx}
      content={wrappedContent}
    />
  );
};
